import { vaultStore } from '$lib/stores/vaultStore'
import { get } from 'svelte/store'

/**
 * File system watcher service
 * Monitors vault folder for changes and notifies listeners
 */
export class FileSystemWatcher {
  private watchers: Map<string, () => void> = new Map()
  private pollInterval: NodeJS.Timeout | null = null
  private lastSnapshot: Map<string, string> = new Map() // filename -> content hash or size
  private isElectron: boolean
  private debounceTimer: NodeJS.Timeout | null = null
  private pendingNotification = false
  private needsPermissionRefresh = false
  private lastPermissionRequest = 0
  private ignoredFiles: Set<string> = new Set() // Files to temporarily ignore
  private ignoredFilesTimers: Map<string, NodeJS.Timeout> = new Map() // Timers for ignored files
  private lastSelfChangeTime = 0 // Track last time we made a change ourselves
  private lastReloadTime = 0 // Track last reload to prevent loops
  private isReloading = false // Prevent concurrent reloads
  private isNativeWatcher = false // Track if using native watcher
  private cleanupFileChange: (() => void) | null = null // Cleanup for file change listener
  private cleanupWatchError: (() => void) | null = null // Cleanup for watch error listener
  private vaultPathSubscription: (() => void) | null = null // Track vault path subscription
  
  constructor() {
    this.isElectron = typeof window !== 'undefined' && 
                      ((window as any).api || 
                       (window as any).electronAPI || 
                       navigator.userAgent.toLowerCase().includes('electron'))
  }
  
  /**
   * Check if watcher is running
   */
  isRunning(): boolean {
    return this.pollInterval !== null || this.isNativeWatcher
  }

  /**
   * Start watching the vault folder
   */
  async start(): Promise<void> {
    const vault = get(vaultStore)
    if (!vault.isInitialized) return

    // Don't start if already running
    if (this.isRunning()) {
      return
    }

    if (this.isElectron) {
      // Check if vault path is ready (critical for Electron file watching)
      if (!vault.vaultPath) {
        // Vault path not ready yet - subscribe and wait
        // Clean up existing subscription if any
        if (this.vaultPathSubscription) {
          this.vaultPathSubscription()
          this.vaultPathSubscription = null
        }

        // Subscribe to vaultStore and start when ready
        this.vaultPathSubscription = vaultStore.subscribe(async (updatedVault) => {
          if (updatedVault?.vaultPath && !this.isRunning()) {
            await this.startElectronWatcher()

            // Cleanup subscription after successful start
            if (this.vaultPathSubscription) {
              this.vaultPathSubscription()
              this.vaultPathSubscription = null
            }
          }
        })
        return
      }

      // Vault path ready - start immediately
      await this.startElectronWatcher()
    } else if (vault.vaultHandle && 'values' in vault.vaultHandle) {
      // Use polling for File System Access API in web
      await this.startPollingWatcher()
    }
  }
  
  /**
   * Stop watching
   */
  stop(): void {
    // Clean up vault path subscription
    if (this.vaultPathSubscription) {
      this.vaultPathSubscription()
      this.vaultPathSubscription = null
    }

    // Stop native watcher if running
    if (this.isNativeWatcher) {
      this.stopElectronWatcher()
    }

    // Stop polling if running
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }

    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    // Clear all ignored files timers
    this.ignoredFilesTimers.forEach(timer => clearTimeout(timer))
    this.ignoredFilesTimers.clear()

    this.watchers.clear()
    this.lastSnapshot.clear()
    this.ignoredFiles.clear()
    this.pendingNotification = false
    this.isReloading = false
  }
  
  /**
   * Add a change listener
   */
  onFileChange(id: string, callback: () => void): void {
    this.watchers.set(id, callback)
  }
  
  /**
   * Remove a change listener
   */
  removeListener(id: string): void {
    this.watchers.delete(id)
  }
  
  /**
   * Start Electron file watcher
   */
  private async startElectronWatcher(): Promise<void> {
    const vault = get(vaultStore)

    // Defensive check: ensure vault path exists
    if (!vault.vaultPath) {
      console.warn('[FileSystemWatcher] Cannot start Electron watcher: vault path not available')
      await this.startPollingWatcher()
      return
    }

    try {
      // Clean up any existing listeners first
      if (this.cleanupFileChange) {
        this.cleanupFileChange()
        this.cleanupFileChange = null
      }
      if (this.cleanupWatchError) {
        this.cleanupWatchError()
        this.cleanupWatchError = null
      }

      // First, set up IPC listeners for file changes
      if ((window as any).api?.onFileChange) {

        // Store the listener so we can clean it up later
        const listener = async (event: any, data: any) => {
          // Accept all files, not just markdown
          if (!data || !data.filename) {
            return
          }

          // Check if this is a self-change that should be ignored
          const filename = data.filename.replace(/\\/g, '/') // Normalize path
          if (this.ignoredFiles.has(filename)) {
            return
          }

          const leafName = filename.split('/').pop() || filename
          if (leafName.startsWith('.')) {
            this.isReloading = false
            return
          }

          // Prevent reload loops
          const now = Date.now()
          if (this.isReloading) {
            return
          }

          if ((now - this.lastReloadTime) < 2000) {
            return
          }

          // Add delay to ensure file system has synced
          await new Promise(resolve => setTimeout(resolve, 300))

          this.isReloading = true
          this.lastReloadTime = now
          
          try {
            // Use the global fileStore instance from window
            const fileStore = (window as any).__fileStore
            if (!fileStore) {
              return
            }

            // data.filename can include subdirectory path like "folder/file.md"
            const filename = data.filename.replace(/\\/g, '/') // Normalize path

          // Handle image file changes separately
          const isImage = /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(filename)
          if (isImage) {
            // Always notify image changes (for both change and delete)
            // The editor will check if the file exists via img.onerror
            import('$lib/stores/imageResizeStore').then(({ notifyImageDeleted }) => {
              notifyImageDeleted(filename)
            })

            // Still notify listeners so FileExplorer refreshes its tree
            this.notifyListeners()
            this.isReloading = false
            return
          }

          // Remove .md extension for consistency with tab IDs
          const fileId = filename.replace(/\.md$/, '')

            // Check if this file is open
            const state = fileStore.getState()
            const openTabs = Array.from(state.tabs.keys()) as string[]

            // Normalize IDs for comparison (same as fileStore.openFile)
            const normalizeId = (id: string) => {
              return id.replace(/^\/+/, '').replace(/\.md$/, '')
            }

            const normalizedFileId = normalizeId(fileId)

            // Find matching tab by checking normalized IDs
            const matchingTabId = openTabs.find((tabId) => {
              const normalizedTabId = normalizeId(tabId)
              // Check if the normalized IDs match or if they end with the same path
              return normalizedTabId === normalizedFileId ||
                     normalizedTabId.endsWith('/' + normalizedFileId) ||
                     normalizedFileId.endsWith('/' + normalizedTabId)
            })

            if (matchingTabId) {
              await fileStore.reloadFile(matchingTabId, true) // Force reload
            }
          } catch (err) {
            // Silently handle errors
          } finally {
            // Reset flag after a delay
            setTimeout(() => {
              this.isReloading = false
            }, 1000)
          }
          
          // Notify other listeners (like FileExplorer)
          this.notifyListeners()
        }

        // Store cleanup function returned by onFileChange
        this.cleanupFileChange = (window as any).api.onFileChange(listener)
      }

      // Set up error listener
      if ((window as any).api?.onFileWatchError) {
        const errorListener = (event: any, data: any) => {
          // Silently handle errors
        }
        // Store cleanup function returned by onFileWatchError
        this.cleanupWatchError = (window as any).api.onFileWatchError(errorListener)
      }
      
      // Start watching the vault path
      if ((window as any).api?.watchFolder) {
        const result = await (window as any).api.watchFolder(vault.vaultPath)
        if (result && result.success) {
          this.isNativeWatcher = true
        } else {
          await this.startPollingWatcher()
        }
      } else {
        await this.startPollingWatcher()
      }
    } catch (err) {
      await this.startPollingWatcher()
    }
  }
  
  /**
   * Stop Electron file watcher
   */
  private stopElectronWatcher(): void {
    const vault = get(vaultStore)

    try {
      // Clean up IPC listeners
      if (this.cleanupFileChange) {
        this.cleanupFileChange()
        this.cleanupFileChange = null
      }
      if (this.cleanupWatchError) {
        this.cleanupWatchError()
        this.cleanupWatchError = null
      }

      // Unwatch folder (defensive check for vault path)
      if ((window as any).api?.unwatchFolder && this.isNativeWatcher && vault.vaultPath) {
        (window as any).api.unwatchFolder(vault.vaultPath)
        this.isNativeWatcher = false
      }
    } catch (err) {
      // Silently handle errors
    }
  }
  
  /**
   * Start polling-based watcher for File System Access API
   */
  private async startPollingWatcher(): Promise<void> {
    // Take initial snapshot
    await this.takeSnapshot()

    // Use different polling intervals for Electron and web
    // Electron: slower polling to prevent excessive reloads
    // Web: faster polling for better responsiveness
    const pollInterval = this.isElectron ? 3000 : 1500

    this.pollInterval = setInterval(async () => {
      // Skip if currently reloading (Electron only)
      if (this.isElectron && this.isReloading) {
        return
      }
      await this.checkForChanges()
    }, pollInterval)
  }
  
  /**
   * Take a snapshot of current files
   */
  private async takeSnapshot(): Promise<void> {
    try {
      const files = await vaultStore.listFiles('', true)
      const newSnapshot = new Map<string, string>()

      const trackableImageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp']

      for (const file of files) {
        if (file.type === 'file') {
          const lowerName = file.name.toLowerCase()
          const isMarkdown = lowerName.endsWith('.md')
          const isImage = trackableImageExtensions.some(ext => lowerName.endsWith(ext))

          if (!isMarkdown && !isImage) {
            continue
          }

          if (isMarkdown) {
            try {
              // Read first 1KB of file content as a fingerprint
              // Pass silent flag to avoid error logging for each file
              const content = await vaultStore.readFile(file.path, true)
              const fingerprint = content.substring(0, 1024) + '::' + content.length
              newSnapshot.set(file.path, fingerprint)
            } catch (err) {
              // If we can't read due to permissions, track as needing refresh
              if (err instanceof DOMException && err.name === 'NotAllowedError') {
                newSnapshot.set(file.path, 'permission_needed')
              } else {
                // If can't read for other reasons, just track existence
                newSnapshot.set(file.path, 'exists')
              }
            }
          } else {
            // Track image file existence with size/mtime fingerprint
            const fingerprint = `image::${file.size ?? 'na'}::${file.modified ?? 'na'}`
            newSnapshot.set(file.path, fingerprint)
          }
        } else if (file.type === 'directory') {
          // Track directories so newly added folders trigger refresh
          const dirKey = file.path.endsWith('/') ? file.path : `${file.path}/`
          const fingerprint = `dir::${file.modified ?? 'na'}`
          newSnapshot.set(dirKey, fingerprint)
        }
      }

      this.lastSnapshot = newSnapshot
    } catch (err) {
      // Silently handle snapshot errors
    }
  }
  
  /**
   * Check for changes by comparing snapshots
   */
  private async checkForChanges(): Promise<void> {
    try {
      const files = await vaultStore.listFiles('', true)
      const currentSnapshot = new Map<string, string>()
      const trackableImageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp']
      
      // Build current snapshot with content fingerprints
      for (const file of files) {
        if (file.type === 'file') {
          const lowerName = file.name.toLowerCase()
          const isMarkdown = lowerName.endsWith('.md')
          const isImage = trackableImageExtensions.some(ext => lowerName.endsWith(ext))

          if (!isMarkdown && !isImage) {
            continue
          }

          if (isMarkdown) {
            try {
              // Read first 1KB of file content as a fingerprint
              const content = await vaultStore.readFile(file.path)
              const fingerprint = content.substring(0, 1024) + '::' + content.length
              currentSnapshot.set(file.path, fingerprint)
            } catch (err) {
              // If we can't read due to permissions, we might need user interaction
              if (err instanceof DOMException && err.name === 'NotAllowedError') {
                this.needsPermissionRefresh = true
                currentSnapshot.set(file.path, 'permission_needed')
                
                // Only request permission once every 30 seconds to avoid spam
                const now = Date.now()
                if (now - this.lastPermissionRequest > 30000) {
                  this.lastPermissionRequest = now
                }
              } else {
                // If can't read, just track existence
                currentSnapshot.set(file.path, 'exists')
              }
            }
          } else {
            const fingerprint = `image::${file.size ?? 'na'}::${file.modified ?? 'na'}`
            currentSnapshot.set(file.path, fingerprint)
          }
        } else if (file.type === 'directory') {
          const dirKey = file.path.endsWith('/') ? file.path : `${file.path}/`
          const fingerprint = `dir::${file.modified ?? 'na'}`
          currentSnapshot.set(dirKey, fingerprint)
        }
      }
      
      // Check for added, removed, or modified files
      const lastFiles = Array.from(this.lastSnapshot.keys())
      const currentFiles = Array.from(currentSnapshot.keys())

      // Filter out ignored files (self-changes) in all environments
      const added = currentFiles.filter(f => {
        if (!lastFiles.includes(f)) {
          return !this.ignoredFiles.has(f)
        }
        return false
      })

      const removed = lastFiles.filter(f => {
        if (!currentFiles.includes(f)) {
          return !this.ignoredFiles.has(f)
        }
        return false
      })

      const modified = currentFiles.filter(f => {
        // Skip ignored files
        if (this.ignoredFiles.has(f)) return false
        const lastFingerprint = this.lastSnapshot.get(f)
        const currentFingerprint = currentSnapshot.get(f)
        return lastFingerprint && currentFingerprint && lastFingerprint !== currentFingerprint
      })
      
      // Always update snapshot, even for self-changes
      this.lastSnapshot = currentSnapshot
      
      // Only notify if there are external changes
      if (added.length > 0 || removed.length > 0 || modified.length > 0) {
        this.lastChangedCount = modified.length + added.length + removed.length
        
        // Don't auto-reload in web - show modal instead
        if (this.isElectron) {
          // Prevent reload loops - only reload if not currently reloading
          // and enough time has passed since last reload
          const now = Date.now()
          if (!this.isReloading && (now - this.lastReloadTime > 3000)) {
            this.isReloading = true
            this.lastReloadTime = now
            
            // In Electron, auto-reload files
            try {
              const { fileStore } = await import('$lib/stores/fileStore')
              
              // Only reload files that actually changed
              const reloadPromises = modified.map(async (filePath) => {
                // Remove .md extension to get the document ID
                const id = filePath.replace(/\.md$/, '')
                const state = fileStore.getState()
                if (state.tabs.has(id)) {
                  await fileStore.reloadFile(id)
                }
              })
              
              await Promise.all(reloadPromises)
            } catch (err) {
              // Silently handle errors
            } finally {
              // Allow reloading again after a delay
              setTimeout(() => {
                this.isReloading = false
              }, 1500)
            }
          }
        }
        
        // Notify listeners (will trigger modal in web)
        this.notifyListeners()
      }
    } catch (err) {
      // Silently handle errors
    }
  }
  
  /**
   * Check if permission refresh is needed
   */
  needsPermission(): boolean {
    return this.needsPermissionRefresh
  }
  
  /**
   * Request permission refresh from user
   */
  async requestPermissionRefresh(): Promise<boolean> {
    try {
      const vault = get(vaultStore)
      if (!vault.vaultHandle) return false
      
      // Request permission by attempting to query permissions
      // This will prompt the user if needed
      const options: FileSystemPermissionDescriptor = { mode: 'read' }
      const permission = await vault.vaultHandle.requestPermission?.(options)
      
      if (permission === 'granted') {
        this.needsPermissionRefresh = false
        // Take a new snapshot after getting permission
        await this.takeSnapshot()
        return true
      } else {
        return false
      }
    } catch (err) {
      return false
    }
  }
  
  /**
   * Get the number of changed files in the last check
   */
  getChangedFilesCount(): number {
    return this.lastChangedCount || 0
  }
  
  /**
   * Clear the changed files count
   */
  clearChangedCount(): void {
    this.lastChangedCount = 0
  }
  
  /**
   * Mark that we're about to save a file (so we don't alert ourselves)
   */
  markSelfChange(filePath: string): void {
    // Clear existing timer for this file if any
    const existingTimer = this.ignoredFilesTimers.get(filePath)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // Add to ignored files
    this.ignoredFiles.add(filePath)
    this.lastSelfChangeTime = Date.now()

    // Set new timer to remove after 5 seconds
    const timer = setTimeout(() => {
      this.ignoredFiles.delete(filePath)
      this.ignoredFilesTimers.delete(filePath)
    }, 5000)

    this.ignoredFilesTimers.set(filePath, timer)
  }
  
  /**
   * Mark multiple files as self-changed
   */
  markSelfChanges(filePaths: string[]): void {
    filePaths.forEach(path => this.markSelfChange(path))
  }
  
  private lastChangedCount = 0
  
  /**
   * Notify all listeners of changes with debouncing
   */
  private notifyListeners(): void {
    // Debounce notifications to avoid multiple rapid updates
    if (this.debounceTimer) {
      this.pendingNotification = true
      return
    }
    
    // Set debounce timer for 300ms
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      
      // If there were pending notifications during the debounce period, trigger one more
      if (this.pendingNotification) {
        this.pendingNotification = false
        this.notifyListeners()
        return
      }
      
      // Dispatch custom event for file explorer
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('file-system-changed'))
      }
      
      // Call individual listeners
      this.watchers.forEach(callback => {
        try {
          callback()
        } catch (err) {
          // Silently handle listener errors
        }
      })
    }, 300)
  }
}

// Singleton instance - store in window to survive hot reloads
declare global {
  interface Window {
    __fileSystemWatcher?: FileSystemWatcher
  }
}

export function getFileSystemWatcher(): FileSystemWatcher {
  // Use window-scoped instance to survive hot reloads
  if (typeof window !== 'undefined') {
    if (!window.__fileSystemWatcher) {
      // Before creating new instance, clean up any existing one
      if ((window as any).__fileSystemWatcherCleanup) {
        ;(window as any).__fileSystemWatcherCleanup()
      }
      
      // Clean up any orphaned event listeners
      if ((window as any).__fileSystemWatcherListeners) {
        const listeners = (window as any).__fileSystemWatcherListeners
        listeners.forEach((listener: any) => {
          if (listener.remove) listener.remove()
        })
        delete (window as any).__fileSystemWatcherListeners
      }
      
      window.__fileSystemWatcher = new FileSystemWatcher()
      // Store cleanup function
      ;(window as any).__fileSystemWatcherCleanup = () => {
        window.__fileSystemWatcher?.stop()
      }
    }
    return window.__fileSystemWatcher
  }
  // Fallback for SSR
  return new FileSystemWatcher()
}
